"use client";

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PortalIcon } from '@/components/PortalIcon';
import { useConsent } from '@/contexts/ConsentContext';
import { supabase } from '@/lib/supabase';

// Bucket C: mandatory baseline consent under GDPR Art. 9(2)(a).
// User cannot use the app without this; declining signs them out.
export const FirstRunConsentModal: React.FC = () => {
  const { setConsent } = useConsent();
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!agreed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await setConsent('C', true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await supabase.auth.signOut();
      window.location.href = '/login';
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        showCloseButton={false}
        className="max-w-md p-0 overflow-hidden"
      >
        <div className="relative px-8 pt-10 pb-8 text-center">
          {/* Brand anchor with soft pacific halo. */}
          <div className="relative mx-auto w-fit">
            <div className="absolute inset-0 -m-4 rounded-full bg-pacific-500/20 blur-2xl" aria-hidden />
            <div className="relative rounded-full bg-graphite-900/70 p-4 ring-1 ring-pacific-500/20 backdrop-blur-xs">
              <PortalIcon size={40} />
            </div>
          </div>

          <DialogTitle className="mt-6 text-2xl font-bold tracking-tight text-fg">
            One thing first
          </DialogTitle>

          <p className="mt-3 text-sm leading-relaxed text-fg-secondary max-w-sm mx-auto">
            El Portal turns what you log into insights only you see. Since that includes how you
            feel, EU rules ask for your{' '}
            <span className="text-fg font-medium">explicit consent</span> before anything is stored.
          </p>

          {/* Quiet promise list, typography only. */}
          <ul className="mt-6 space-y-2 text-left max-w-sm mx-auto">
            <li className="flex items-start gap-3 text-sm text-fg-secondary">
              <Check size={16} className="mt-0.5 text-pacific-400 flex-shrink-0" />
              <span>Stays only with you. We never sell, share, or read it.</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-fg-secondary">
              <Check size={16} className="mt-0.5 text-pacific-400 flex-shrink-0" />
              <span>Won&apos;t be used to train AI without asking you separately.</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-fg-secondary">
              <Check size={16} className="mt-0.5 text-pacific-400 flex-shrink-0" />
              <span>Revoke any time in Settings.</span>
            </li>
          </ul>
        </div>

        {/* Consent line and action row. */}
        <div className="border-t border-border bg-graphite-950/40 px-8 py-5 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-pacific-500 cursor-pointer"
            />
            <span className="text-sm text-fg leading-snug">
              Yes, store my mood, habit, and cycle data so El Portal can work for me.
            </span>
          </label>

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
              {error}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={handleDecline} disabled={busy}>
              Decline
            </Button>
            <Button
              variant="cta"
              size="sm"
              onClick={handleAccept}
              disabled={!agreed || busy}
            >
              {busy ? 'Saving' : 'Begin'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
